import { useEffect, useState } from "react";

/**
 * Detects whether the app is running as an installed PWA
 * OR as a native Capacitor app (Android/iOS wrapper).
 *
 * Signals we treat as "installed":
 *   - Capacitor WebView (native app — check first)
 *   - display-mode: standalone (Android/desktop PWA)
 *   - navigator.standalone (iOS home-screen PWA)
 */
function detectInstalled() {
  if (typeof window === "undefined") return true;
  // Native Capacitor app — most reliable signal for Android/iOS wrapper builds
  if (window.Capacitor?.isNativePlatform?.()) return true;
  // Older Capacitor: `Capacitor.platform` is "android"/"ios" and NOT "web"
  const platform = window.Capacitor?.platform;
  if (platform && platform !== "web") return true;
  // Standard PWA signals
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

export function useIsPWAInstalled() {
  const [installed, setInstalled] = useState(detectInstalled);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia?.("(display-mode: standalone)");
    if (!mql) return;
    const handler = () => setInstalled(detectInstalled());
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);
  return installed;
}

/** Captures the beforeinstallprompt event so we can trigger install on demand (Android/Chrome). */
export function useInstallPrompt() {
  const [event, setEvent] = useState(null);
  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    const onInstalled = () => setEvent(null);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  return {
    event,
    available: !!event,
    prompt: async () => {
      if (!event) return null;
      event.prompt();
      const choice = await event.userChoice;
      setEvent(null);
      return choice?.outcome;
    },
  };
}

export function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}
